import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, Plus, Upload, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { uploadService } from '@/services/upload.service';

export interface BeforeAfterImage {
  /** S3 key for before image (optional - supports unpaired images) */
  before?: string;
  /** S3 key for after image (optional - supports unpaired images) */
  after?: string;
  /** @deprecated Use beforeCaption and afterCaption instead */
  caption?: string;
  /** Caption for the existing/before image */
  beforeCaption?: string;
  /** Caption for the after image */
  afterCaption?: string;
  /** Hide the Existing tag on the before image */
  hideBeforeTag?: boolean;
  /** Hide the After tag on the after image */
  hideAfterTag?: boolean;
}

export interface BeforeAfterDisplayUrls {
  before?: string;
  after?: string;
}

interface BeforeAfterUploaderProps {
  /** Array of before/after image pairs (S3 keys) */
  value: BeforeAfterImage[];
  /** Called with updated array of pairs (S3 keys) */
  onChange: (images: BeforeAfterImage[]) => void;
  entityId?: string;
  label?: string;
  maxPairs?: number;
  className?: string;
  /** Display URLs for each pair (full CloudFront URLs) - use these for showing images */
  displayUrls?: BeforeAfterDisplayUrls[];
  /** Minimum recommended width for quality display */
  recommendedMinWidth?: number;
  /** Minimum recommended height for quality display */
  recommendedMinHeight?: number;
}

// Helper to check image dimensions
const checkImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
};

interface SortableRowProps {
  id: string;
  index: number;
  entry: BeforeAfterImage;
  warning?: string;
  renderUploadBox: (
    index: number,
    type: 'before' | 'after',
    imageKey?: string,
    showRemoveButton?: boolean
  ) => React.ReactNode;
  onCaptionChange: (index: number, type: 'before' | 'after', caption: string) => void;
  onHideTagChange: (index: number, type: 'before' | 'after', hide: boolean) => void;
  onRemoveEntry: (index: number) => void;
}

function SortableRow({
  id,
  index,
  entry,
  warning,
  renderUploadBox,
  onCaptionChange,
  onHideTagChange,
  onRemoveEntry,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative rounded-[8px] border border-[#e5e5e5] bg-[#fafafa] p-[12px]"
    >
      <button
        type="button"
        onClick={() => onRemoveEntry(index)}
        className="absolute -right-[8px] -top-[8px] flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#d4183d] text-white shadow-md transition-colors hover:bg-[#b91c1c]"
        title="Remove entire entry"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-[16px]">
        {/* Drag Handle */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mt-[28px] cursor-grab text-[#8d8c8f] hover:text-[#181818] active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical size={18} />
        </button>

        {/* Existing/Before Image */}
        <div className="text-center">
          <span className="mb-[4px] block text-[10px] font-medium uppercase text-[#8d8c8f]">
            Existing
          </span>
          {renderUploadBox(index, 'before', entry.before, !!entry.before)}
          {entry.before && (
            <>
              <input
                type="text"
                placeholder="Caption"
                value={entry.beforeCaption || entry.caption || ''}
                onChange={(e) => onCaptionChange(index, 'before', e.target.value)}
                className="mt-[8px] h-[32px] w-[120px] rounded-[6px] border border-[#e5e5e5] bg-white px-[8px] text-[11px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              />
              <label className="mt-[6px] flex cursor-pointer items-center justify-center gap-[4px]">
                <input
                  type="checkbox"
                  checked={entry.hideBeforeTag || false}
                  onChange={(e) => onHideTagChange(index, 'before', e.target.checked)}
                  className="h-[14px] w-[14px] rounded border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
                />
                <span className="text-[10px] text-[#8d8c8f]">Hide image tag</span>
              </label>
            </>
          )}
        </div>

        <div className="mt-[40px] text-[24px] text-[#e5e5e5]">&rarr;</div>

        {/* After Image */}
        <div className="text-center">
          <span className="mb-[4px] block text-[10px] font-medium uppercase text-[#8d8c8f]">
            After
          </span>
          {renderUploadBox(index, 'after', entry.after, !!entry.after)}
          {entry.after && (
            <>
              <input
                type="text"
                placeholder="Caption"
                value={entry.afterCaption || entry.caption || ''}
                onChange={(e) => onCaptionChange(index, 'after', e.target.value)}
                className="mt-[8px] h-[32px] w-[120px] rounded-[6px] border border-[#e5e5e5] bg-white px-[8px] text-[11px] placeholder-[#8d8c8f] focus:border-[#055135] focus:outline-none"
              />
              <label className="mt-[6px] flex cursor-pointer items-center justify-center gap-[4px]">
                <input
                  type="checkbox"
                  checked={entry.hideAfterTag || false}
                  onChange={(e) => onHideTagChange(index, 'after', e.target.checked)}
                  className="h-[14px] w-[14px] rounded border-[#e5e5e5] text-[#055135] focus:ring-[#055135]"
                />
                <span className="text-[10px] text-[#8d8c8f]">Hide image tag</span>
              </label>
            </>
          )}
        </div>
      </div>
      {warning && <p className="mt-[8px] text-[11px] text-[#b45309]">{warning}</p>}
    </div>
  );
}

export function BeforeAfterUploader({
  value = [],
  onChange,
  entityId,
  label = 'Existing/After Images',
  maxPairs = 5,
  className = '',
  displayUrls = [],
  recommendedMinWidth,
  recommendedMinHeight,
}: BeforeAfterUploaderProps) {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [uploadingType, setUploadingType] = useState<'before' | 'after' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track warnings per entry index
  const [warnings, setWarnings] = useState<Record<number, string>>({});
  // Track display URLs for newly uploaded files (key -> displayUrl mapping)
  const [localDisplayUrls, setLocalDisplayUrls] = useState<Record<string, string>>({});

  // Stable IDs for sortable items
  const itemIds = useMemo(() => value.map((_, i) => `ba-${i}`), [value.length]);

  // DnD sensors with distance constraint to avoid accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Get display URL for a key
  const getDisplayUrl = (key: string, index: number, type: 'before' | 'after'): string => {
    // First check if we have a corresponding displayUrl from props
    const pairUrl = displayUrls[index]?.[type];
    if (pairUrl) {
      return pairUrl;
    }
    // Otherwise check local state for newly uploaded files
    if (localDisplayUrls[key]) {
      return localDisplayUrls[key];
    }
    // Fallback to key itself (shouldn't normally happen)
    return key;
  };

  const handleUpload = useCallback(
    async (file: File, index: number, type: 'before' | 'after') => {
      setUploadingIndex(index);
      setUploadingType(type);
      setError(null);
      // Clear warning for this index
      setWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[index];
        return newWarnings;
      });

      // Check image dimensions for quality warning
      if (recommendedMinWidth || recommendedMinHeight) {
        if (file.type.startsWith('image/')) {
          const { width, height } = await checkImageDimensions(file);
          if (width > 0 && height > 0) {
            const isBelowWidth = recommendedMinWidth && width < recommendedMinWidth;
            const isBelowHeight = recommendedMinHeight && height < recommendedMinHeight;
            if (isBelowWidth || isBelowHeight) {
              setWarnings((prev) => ({
                ...prev,
                [index]: `Low resolution image (${width}×${height}px). For best quality, use at least ${recommendedMinWidth || 0}×${recommendedMinHeight || 0}px. The image may appear blurry or distorted.`,
              }));
            }
          }
        }
      }

      try {
        const { key, fileUrl } = await uploadService.uploadFile(
          file,
          'listings/before-after',
          entityId
        );

        // Track the display URL locally
        setLocalDisplayUrls((prev) => ({ ...prev, [key]: fileUrl }));

        const newValue = [...value];
        if (index === value.length) {
          // New image entry
          const newEntry: BeforeAfterImage = {
            before: type === 'before' ? key : undefined,
            after: type === 'after' ? key : undefined,
            beforeCaption: '',
            afterCaption: '',
            hideBeforeTag: false,
            hideAfterTag: false,
          };
          newValue.push(newEntry);
        } else {
          // Update existing entry
          const existingEntry = newValue[index];
          if (existingEntry) {
            const updatedEntry: BeforeAfterImage = {
              ...existingEntry,
              before: type === 'before' ? key : existingEntry.before,
              after: type === 'after' ? key : existingEntry.after,
            };
            newValue[index] = updatedEntry;
          }
        }
        onChange(newValue);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploadingIndex(null);
        setUploadingType(null);
      }
    },
    [entityId, onChange, value, recommendedMinWidth, recommendedMinHeight]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number, type: 'before' | 'after') => {
      const file = e.target.files?.[0];
      if (file) {
        handleUpload(file, index, type);
      }
      e.target.value = '';
    },
    [handleUpload]
  );

  const handleCaptionChange = useCallback(
    (index: number, type: 'before' | 'after', caption: string) => {
      const newValue = [...value];
      const existingEntry = newValue[index];
      if (existingEntry) {
        const updatedEntry: BeforeAfterImage = {
          ...existingEntry,
          beforeCaption: type === 'before' ? caption : existingEntry.beforeCaption,
          afterCaption: type === 'after' ? caption : existingEntry.afterCaption,
        };
        newValue[index] = updatedEntry;
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  const handleHideTagChange = useCallback(
    (index: number, type: 'before' | 'after', hide: boolean) => {
      const newValue = [...value];
      const existingEntry = newValue[index];
      if (existingEntry) {
        const updatedEntry: BeforeAfterImage = {
          ...existingEntry,
          hideBeforeTag: type === 'before' ? hide : existingEntry.hideBeforeTag,
          hideAfterTag: type === 'after' ? hide : existingEntry.hideAfterTag,
        };
        newValue[index] = updatedEntry;
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  const handleRemoveImage = useCallback(
    (index: number, type: 'before' | 'after') => {
      const newValue = [...value];
      const existingEntry = newValue[index];
      if (existingEntry) {
        const updatedEntry: BeforeAfterImage = {
          ...existingEntry,
          before: type === 'before' ? undefined : existingEntry.before,
          after: type === 'after' ? undefined : existingEntry.after,
          beforeCaption: type === 'before' ? '' : existingEntry.beforeCaption,
          afterCaption: type === 'after' ? '' : existingEntry.afterCaption,
          hideBeforeTag: type === 'before' ? false : existingEntry.hideBeforeTag,
          hideAfterTag: type === 'after' ? false : existingEntry.hideAfterTag,
        };
        // If both images are now empty, remove the entire entry
        if (!updatedEntry.before && !updatedEntry.after) {
          newValue.splice(index, 1);
        } else {
          newValue[index] = updatedEntry;
        }
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  const handleRemoveEntry = useCallback(
    (index: number) => {
      const newValue = [...value];
      newValue.splice(index, 1);
      onChange(newValue);
      // Clear warning for removed entry and shift remaining warnings
      setWarnings((prev) => {
        const newWarnings: Record<number, string> = {};
        Object.entries(prev).forEach(([key, val]) => {
          const keyNum = parseInt(key, 10);
          if (keyNum < index) {
            newWarnings[keyNum] = val;
          } else if (keyNum > index) {
            newWarnings[keyNum - 1] = val;
          }
        });
        return newWarnings;
      });
    },
    [value, onChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = itemIds.indexOf(active.id as string);
        const newIndex = itemIds.indexOf(over.id as string);
        const newValue = arrayMove([...value], oldIndex, newIndex);
        onChange(newValue);

        // Remap warnings to follow moved items
        setWarnings((prev) => {
          const entries = Object.entries(prev);
          if (entries.length === 0) return prev;
          const reordered: Record<number, string> = {};
          for (const [key, val] of entries) {
            const keyNum = parseInt(key, 10);
            // Determine new position after the move
            let newPos = keyNum;
            if (keyNum === oldIndex) {
              newPos = newIndex;
            } else if (oldIndex < newIndex && keyNum > oldIndex && keyNum <= newIndex) {
              newPos = keyNum - 1;
            } else if (oldIndex > newIndex && keyNum >= newIndex && keyNum < oldIndex) {
              newPos = keyNum + 1;
            }
            reordered[newPos] = val;
          }
          return reordered;
        });
      }
    },
    [value, onChange, itemIds]
  );

  const canAddMore = value.length < maxPairs;

  const renderUploadBox = useCallback(
    (index: number, type: 'before' | 'after', imageKey?: string, showRemoveButton = true) => {
      const isUploading = uploadingIndex === index && uploadingType === type;
      const displayUrl = imageKey ? getDisplayUrl(imageKey, index, type) : undefined;

      if (imageKey && displayUrl) {
        return (
          <div className="relative h-[100px] w-[120px]">
            <img src={displayUrl} alt={type} className="h-full w-full rounded-[6px] object-cover" />
            <label className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-[6px] bg-black/50 opacity-0 transition-opacity hover:opacity-100">
              <Upload size={16} className="text-white" />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, index, type)}
                className="hidden"
              />
            </label>
            {showRemoveButton && (
              <button
                type="button"
                onClick={() => handleRemoveImage(index, type)}
                className="absolute -right-[6px] -top-[6px] flex h-[20px] w-[20px] items-center justify-center rounded-full bg-[#d4183d] text-white shadow-md transition-colors hover:bg-[#b91c1c]"
              >
                <X size={12} />
              </button>
            )}
          </div>
        );
      }

      return (
        <label className="flex h-[100px] w-[120px] cursor-pointer flex-col items-center justify-center rounded-[6px] border-2 border-dashed border-[#e5e5e5] bg-[#fafafa] transition-colors hover:border-[#055135]">
          {isUploading ? (
            <Loader2 size={20} className="animate-spin text-[#055135]" />
          ) : (
            <>
              <Upload size={16} className="text-[#8d8c8f]" />
              <span className="mt-[4px] text-[10px] text-[#8d8c8f]">
                {type === 'before' ? 'Existing' : 'After'}
              </span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e, index, type)}
            disabled={isUploading}
            className="hidden"
          />
        </label>
      );
    },
    [
      uploadingIndex,
      uploadingType,
      displayUrls,
      localDisplayUrls,
      handleFileChange,
      handleRemoveImage,
    ]
  );

  return (
    <div className={className}>
      {label && (
        <span className="mb-[8px] block text-[14px] font-medium text-[#181818]">
          {label}
          <span className="ml-[8px] text-[12px] font-normal text-[#8d8c8f]">
            ({value.length}/{maxPairs} images)
          </span>
        </span>
      )}

      <div className="space-y-[16px]">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {value.map((entry, index) => {
              const itemId = itemIds[index] ?? `ba-${index}`;
              return (
                <SortableRow
                  key={itemId}
                  id={itemId}
                  index={index}
                  entry={entry}
                  warning={warnings[index]}
                  renderUploadBox={renderUploadBox}
                  onCaptionChange={handleCaptionChange}
                  onHideTagChange={handleHideTagChange}
                  onRemoveEntry={handleRemoveEntry}
                />
              );
            })}
          </SortableContext>
        </DndContext>

        {canAddMore && (
          <div className="flex items-center gap-[16px] rounded-[8px] border-2 border-dashed border-[#e5e5e5] p-[12px]">
            <div className="text-center">
              <span className="mb-[4px] block text-[10px] font-medium uppercase text-[#8d8c8f]">
                Existing
              </span>
              {renderUploadBox(value.length, 'before', undefined, false)}
            </div>

            <div className="text-[24px] text-[#e5e5e5]">&rarr;</div>

            <div className="text-center">
              <span className="mb-[4px] block text-[10px] font-medium uppercase text-[#8d8c8f]">
                After
              </span>
              {renderUploadBox(value.length, 'after', undefined, false)}
            </div>

            <div className="flex flex-1 items-center justify-center">
              <span className="text-[12px] text-[#8d8c8f]">
                <Plus size={14} className="mr-[4px] inline" />
                Add image (either or both)
              </span>
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-[8px] text-[12px] text-[#d4183d]">{error}</p>}
    </div>
  );
}
